import { paths } from "@kwp/firestore";
import { collection, doc, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { isUnreadActivity, toActivity, type ActivityItem } from "./activity-model";

/** The newest rows still in the inbox; older ones wait for Done on newer ones (rare: rows are per conversation). */
const PAGE = 100;

export type ActivityState = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; items: ActivityItem[] };

/**
 * My chats and tasks still in the inbox (WP-054), live, with whether each is unread. A chat row
 * also listens to my read marker in its channel, so reading it in Chat clears it here.
 */
export function useActivity(): ActivityState & { unread: (item: ActivityItem) => boolean } {
  const { firebase } = useClients();
  const me = useMe();
  const [state, setState] = useState<ActivityState>({ status: "loading" });

  useEffect(() => {
    setState({ status: "loading" });
    return onSnapshot(
      query(collection(firebase.db, paths.activities(me.uid)), where("done", "==", false), orderBy("lastAt", "desc"), limit(PAGE)),
      (snap) =>
        setState({
          status: "ready",
          items: snap.docs.flatMap((d) => {
            const item = toActivity(d.id, d.data());
            return item === null ? [] : [item];
          }),
        }),
      (error) => setState({ status: "error", message: error.message }),
    );
  }, [firebase.db, me.uid]);

  const chats = useMemo(
    () => (state.status === "ready" ? state.items.filter((i) => i.kind === "chat").map((i) => `${i.workspaceId}/${i.targetId}`) : []),
    [state],
  );
  const markers = useChatReadMarkers(chats, me.uid);
  const unread = useMemo(() => (item: ActivityItem) => isUnreadActivity(item, markers.get(`${item.workspaceId}/${item.targetId}`)), [markers]);
  return { ...state, unread };
}

/** My read marker in each `workspace/channel` (null: never read; absent until it answers). */
function useChatReadMarkers(keys: readonly string[], uid: string): ReadonlyMap<string, Date | null> {
  const { firebase } = useClients();
  const [markers, setMarkers] = useState<ReadonlyMap<string, Date | null>>(new Map());
  const key = [...new Set(keys)].sort().join(",");

  useEffect(() => {
    const stops = (key === "" ? [] : key.split(",")).map((k) => {
      const [workspaceId, channelId] = k.split("/") as [string, string];
      return onSnapshot(
        doc(firebase.db, paths.readMarker(workspaceId, channelId, uid)),
        (snap) => {
          const value: unknown = snap.get("lastReadAt", { serverTimestamps: "estimate" });
          const at = value !== null && typeof value === "object" && "toDate" in value ? (value as { toDate(): Date }).toDate() : null;
          setMarkers((all) => new Map(all).set(k, at));
        },
        (e) => console.error(e),
      );
    });
    return () => stops.forEach((stop) => stop());
  }, [firebase.db, uid, key]);

  return markers;
}
